import { Fragment, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Select, { components } from "react-select";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { NoDataComponent } from "../../../../../components/common/NoDataComponent/NoDataComponent";
import { Pagination } from "../../../../../components/common/Pagination/Pagination";
import { ApiService } from "../../../../../services/ApiService";
import { errorToast } from "../../../../../utils/helpers/customToast.helper";
import { clearObjectEmptyValues } from "../../../../../utils/helpers/clearObjectEmptyValues";
import { moveTo } from "../../../../../utils/helpers/moveTo";
import { IconSvg } from "../../../../../utils/constants/icons/iconSvg.constant";
import { getBorderColor } from "../../../../../utils/helpers/getBorderColor";
import { useDispatchedActions, useTranslation } from "../../../../../hooks";
import { FormatCellFilter } from "../Table/components/TableColumns/FormatCellFilter";
import { TableRowLoader } from "../Table/components/ui/TableRowLoader/TableRowLoader";
import { FormatCellIP } from "../Table/components/TableColumns";
import { FormatCellProtocol } from "../Table/components/TableColumns/FormatCellProtocol";
import { rowPerPageOptions } from "../../../../../utils/constants";
import "../Table/Table.scss";
import { useGetIpReplacementOptions } from "../../../../../hooks/useGetIpReplacementOptions";
import { Button } from "../../../../../components/ui/Buttons/Button/Button";
import { fetchingDelayTime } from "../../../../../utils/constants/fetchingDelayTime";

export const ReplacedIpsTable = ({ controller }) => {
  const { lang } = useParams();

  const { setSelectedRows } = useDispatchedActions();

  const {
    select: { noOptionsMessage },
    cabinet: { balance, orders: { replacedIpsTable }, orders },
    main: { buyBtn }
  } = useTranslation();

  const {
    selectedFolder
  } = useSelector((state) => state.cabinet);
  const { activeData } = useSelector((state) => state.content);

  const [localSize, setLocalSize] = useState(10);
  const [localCurrentPage, setLocalCurrentPage] = useState(1);

  const [tableData, setTableData] = useState({});
  const [isTableLoaded, setIsTableLoaded] = useState(false);

  const [searchParams] = useSearchParams();

  const [replaceReasons, replaceOptions] = useGetIpReplacementOptions();

  const navigate = useNavigate();

  const dateStart = searchParams.get("dateStart") || null;
  const dateEnd = searchParams.get("dateEnd") || null;

  const { skin } = useSelector((state) => state.header);

  const ref = useRef();

  const fetchTableData = async ({ page, size, proxyType, filterParams, sort, controller, filterByDateParams }) => {
    const loadingInterval = setTimeout(() => setIsTableLoaded(false), fetchingDelayTime);

    try {
      clearObjectEmptyValues(filterParams);
      clearObjectEmptyValues(sort);

      const params = { ...filterParams, proxyType };

      const { data, status } = await ApiService.getAllReplacedUserTableData(page, size, params, sort, controller, filterByDateParams);

      if (status !== 200) {
        throw new Error();
      }

      setTableData(data);
    } catch (err) {
      errorToast("Something went wrong");
    } finally {
      clearTimeout(loadingInterval);
      setIsTableLoaded(true);
    }
  };

  useEffect(() => {
    if (ref.current) {
      ref.current.select.setValue({ label: `${localSize} ${balance?.lines}`, value: localSize });
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const filterParams = {
    country: searchParams.get("country"),
    runningOut: null,
    description: null

  };

  const filterByDateParams = {
    filterOffDate: !!dateStart || !!dateEnd,
    filterOffDateStart: dateStart,
    filterOffDateEnd: dateEnd
  };

  const sort = {
    [searchParams.get("sort")]: searchParams.get("order")
  };

  const isPaginationVisible = tableData?.totalElements > 10;
  const TOP_OF_TABLE = 420;

  // Page change handler
  const handlePageChange = (page) => {
    setLocalCurrentPage(page);
    moveTo(0, TOP_OF_TABLE);
    fetchTableData({
      page: page - 1,
      size: localSize,
      proxyType: activeData?.proxy?.code,
      selectedFolder,
      filterParams,
      sort,
      filterByDateParams
    });
  };

  // Rows size handler
  const handlePerRowsChange = (newPerPage) => {
    if (newPerPage?.value === localSize) return;

    setLocalSize(newPerPage?.value);
    setLocalCurrentPage(1);
    fetchTableData({
      page: 0,
      size: Number(newPerPage?.value),
      proxyType: activeData?.proxy?.code,
      selectedFolder,
      filterParams,
      sort,
      filterByDateParams
    });
  };

  // Define rows per page options
  const defineRowsPerPageOptions = () =>
    [
      ...rowPerPageOptions.filter((item) => item < tableData?.totalElements),
      tableData?.totalElements
    ].map((item) => ({ label: `${item} ${balance?.lines}`, value: item }));

  // first fetching table data
  useEffect(() => {
    if (activeData?.proxy?.code) {
      setLocalSize(10);
      setLocalCurrentPage(1);
      setSelectedRows([]);
      fetchTableData({
        page: 0,
        size: 10,
        proxyType: activeData?.proxy?.code,
        selectedFolder,
        filterParams,
        sort,
        controller,
        filterByDateParams
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeData?.proxy?.code, selectedFolder, searchParams]);

  const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
      <IconSvg tag="triangleDown12" />
    </components.DropdownIndicator>
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    return `${day}.${month}.${year}, ${hours}:${minutes}`;
  };

  const getReasonOptions = (reason, customReason) => {
    if (reason === replaceReasons.custom) return customReason;

    const option = replaceOptions.find(({ value }) => value === reason);

    return option?.label || "";
  };

  return (
    <div className="table__container">
      <div className="table-contain">
        <div className="table-box">
          <table className="table">
            <thead className="table-head">
            <tr className="table-head-tr">
              <th className="table-head-tr-item ip">
                <div className="table__new-ip">{replacedIpsTable?.newIp}</div>
                <div className="table__new-ip-filter"><FormatCellFilter type="replaced" /></div>
              </th>
              <th className="table-head-tr-item replaced-https">
                {orders?.column?.ip?.http}
              </th>
              <th className="table-head-tr-item replaced-socks">
                {orders?.column?.ip?.socks}
              </th>
              <th className="table-head-tr-item removed-ip removed-ip--head">
                {replacedIpsTable?.removedIP}
              </th>
              <th className="table-head-tr-item replaced-date replaced-date--head">
                {replacedIpsTable?.replacedDate}
              </th>
              <th className="table-head-tr-item reasons reasons--head">
                {replacedIpsTable?.reasons}
              </th>
            </tr>
            </thead>
            <tbody className="table-body">
            { !isTableLoaded ? (
              Array(10).fill("_").map((_, idx) => (
                <TableRowLoader key={idx} />
              ))
            ) : (
              <>
                {tableData?.totalElements !== 0 ? (
                  tableData.content.map((row) => (
                    <Fragment key={row?.id}>
                      <tr className="table-body-tr">
                        <td className="table-body-tr-item ip">
                          <FormatCellIP row={row} type="replaced" />
                        </td>
                        <td className="table-body-tr-item replaced-https">
                          <FormatCellProtocol order={row} protocol="HTTP" />
                        </td>
                        <td className="table-body-tr-item replaced-socks">
                          <FormatCellProtocol order={row} protocol="SOCKS" />
                        </td>
                        <td className="table-body-tr-item removed-ip">
                          {row?.oldIp}
                        </td>
                        <td className="table-body-tr-item replaced-date">
                          {formatDate(row?.replaceDate)}
                        </td>
                        <td className="table-body-tr-item reason">
                          {getReasonOptions(row?.reason, row?.customReason)}
                        </td>
                      </tr>
                    </Fragment>
                  ))
                ) : (
                  <tr className="table-body-tr nodata">
                    <td className="table-body-noData">
                      <NoDataComponent>
                        <Button tab="confirm" onClick={() => navigate("?tab=new-order")}>{buyBtn}</Button>
                      </NoDataComponent>
                    </td>
                  </tr>
                )}
              </>
            )}
            </tbody>
          </table>
        </div>
        <div className="table__pagination table__pagination--renewal">
          {isTableLoaded && isPaginationVisible && (
            <Pagination
              totalPages={tableData?.totalPages}
              currentPage={localCurrentPage}
              onPageChange={handlePageChange}
              hideOnSinglePage={false}
            />
          )}
          {isTableLoaded && isPaginationVisible && (
            <Select
              className={"modal__select"}
              ref={ref}
              classNamePrefix="modal__select"
              options={defineRowsPerPageOptions()}
              defaultValue={{
                label: `${localSize} ${balance?.lines}`,
                value: localSize
              }}
              menuPlacement="bottom"
              isSearchable={false}
              // placeholder={placeholder}
              onChange={handlePerRowsChange}
              noOptionsMessage={() => noOptionsMessage}
              components={{ DropdownIndicator }}
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: getBorderColor(state, skin)
                })
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
